import React, { createContext, useContext, useState, useCallback } from 'react';
import { Client, CreateClientDTO, UpdateClientDTO } from '../types/client';
import * as clientApi from '../api/clients';
import { AxiosError } from 'axios';

interface ClientContextType {
  clients: Client[];
  selectedClient: Client | null;
  setSelectedClient: (client: Client | null) => void;
  loading: boolean;
  error: string | null;
  fetchClients: () => Promise<void>;
  fetchClient: (id: string) => Promise<Client>;
  createClient: (data: CreateClientDTO) => Promise<Client>;
  updateClient: (id: string, data: UpdateClientDTO) => Promise<void>;
  deleteClient: (id: string) => Promise<void>;
  clearSelectedClient: () => void;
  clearError: () => void;
}

const ClientContext = createContext<ClientContextType | undefined>(undefined);

export const useClientContext = () => {
  const context = useContext(ClientContext);
  if (!context) {
    throw new Error('useClientContext must be used within a ClientProvider');
  }
  return context;
};

export const ClientProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [clients, setClients] = useState<Client[]>([]);
  const [selectedClient, setSelectedClient] = useState<Client | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleApiError = (err: unknown): Error => {
    if (err instanceof Error) {
      return err; // If it's already a properly formatted error from the API, return it
    }
    
    if (err instanceof AxiosError) {
      const errorMessage = err.response?.data?.message || 'An error occurred';
      
      switch (err.response?.status) {
        case 400:
          return new Error(`Invalid request: ${errorMessage}`);
        case 401:
          return new Error('Please log in to continue');
        case 403:
          return new Error('You do not have permission to perform this action');
        case 404:
          return new Error('Client not found');
        case 409:
          return new Error('A client with this email address already exists. Please use a different email.');
        case 422:
          return new Error(`Validation error: ${errorMessage}`);
        case 500:
          return new Error('Server error. Please try again later');
        default:
          return new Error(errorMessage);
      }
    }
    return new Error('An unexpected error occurred. Please try again');
  };

  const fetchClients = useCallback(async () => {
    try {
      const data = await clientApi.getClients();
      setClients(data);
      setError(null);
    } catch (err) {
      const error = handleApiError(err);
      setError(error.message);
      throw error;
    }
  }, []);

  const fetchClient = useCallback(async (id: string) => {
    try {
      
      setLoading(true);
      const data = await clientApi.getClient(id);
      
      setSelectedClient(data);
      setError(null);
      return data;
    } catch (err) {
      
      const error = handleApiError(err);
      setError(error.message);
      throw error;
    } finally {
      setLoading(false);
    }
  }, []);

  const createClient = useCallback(async (data: CreateClientDTO) => {
    try {
      
      setLoading(true);
      const newClient = await clientApi.createClient(data);
      
      setClients(prev => [...prev, newClient]);
      setError(null);
      return newClient;
    } catch (err) {
      
      const error = handleApiError(err);
      setError(error.message);
      throw error;
    } finally {
      setLoading(false);
    }
  }, []);

  const updateClient = useCallback(async (id: string, data: UpdateClientDTO) => {
    try {
      
      setLoading(true);
      const updatedClient = await clientApi.updateClient(id, data);
      
      // Ensure we preserve properties and other nested data that might not be returned by the API
      const mergedClient = (client: Client) => {
        // Keep the original properties if they exist and aren't in the updated client
        return {
          ...client,
          ...updatedClient,
          // Preserve properties if they're not in the updated client
          properties: updatedClient.properties || client.properties
        };
      };
      
      setClients(prev => prev.map(client => client._id === id ? mergedClient(client) : client));
      if (selectedClient?._id === id) {
        setSelectedClient(mergedClient(selectedClient));
      }
      setError(null);
    } catch (err) {
      
      const error = handleApiError(err);
      setError(error.message);
      throw error;
    } finally {
      setLoading(false);
    }
  }, [selectedClient]);

  const deleteClient = useCallback(async (id: string) => {
    try {
      
      setLoading(true);
      await clientApi.deleteClient(id);
      
      setClients(prev => prev.filter(client => client._id !== id));
      if (selectedClient?._id === id) {
        setSelectedClient(null);
      }
      setError(null);
    } catch (err) {
      
      const error = handleApiError(err);
      setError(error.message);
      throw error;
    } finally {
      setLoading(false);
    }
  }, [selectedClient]);

  const clearSelectedClient = useCallback(() => {
    
    setSelectedClient(null);
  }, []);

  const clearError = useCallback(() => {
    
    setError(null);
  }, []);

  return (
    <ClientContext.Provider
      value={{
        clients,
        selectedClient,
        setSelectedClient,
        loading,
        error,
        fetchClients,
        fetchClient,
        createClient,
        updateClient,
        deleteClient,
        clearSelectedClient,
        clearError,
      }}
    >
      {children}
    </ClientContext.Provider>
  );
};